/** 反馈提交：构建载荷 → HMAC-SHA256 签名 → POST /feedback。 */
import type { Engine } from '../game/engine';
import { variantForDate } from '../game/variants';
import { API_BASE } from './api';
import { canonicalJSON } from './canonical';
import type { ClientIdentity } from './identity';

export type FeedbackCategory = 'bug' | 'balance' | 'feel' | 'idea' | 'other';

export interface FeedbackContext {
  date?: string;
  mode?: string;
  variant?: string;
  seed?: number;
  score?: number;
  kills?: number;
  time?: number;
  level?: number;
  cause?: string;
}

export interface FeedbackDraft {
  category: FeedbackCategory;
  text: string;
  contact: string;
  context: FeedbackContext;
}

function toHex(buf: ArrayBuffer): string {
  return Array.from(new Uint8Array(buf), (b) =>
    b.toString(16).padStart(2, '0')).join('');
}

function randomNonce(): string {
  const b = new Uint8Array(8);
  crypto.getRandomValues(b);
  return toHex(b.buffer);
}

/** 从当前/最近一局引擎状态抽取白名单上下文。 */
export function contextFromEngine(e: Engine): FeedbackContext {
  const cause = e.death?.cause
    ?? (e.phase === 'complete' ? 'VICTORY' : undefined);
  return {
    date: e.date,
    mode: e.mode,
    variant: variantForDate(e.date).id,
    seed: e.seed,
    score: e.scoreBreakdown?.total ?? 0,
    kills: e.kills,
    time: Math.round(e.time),
    level: e.player.level,
    cause,
  };
}

export async function submitFeedback(
  id: ClientIdentity,
  draft: FeedbackDraft,
): Promise<number> {
  if (!id.secret) throw new Error('missing signing secret');
  const body = {
    token: id.token,
    category: draft.category,
    text: draft.text,
    contact: draft.contact,
    context: draft.context,
    nonce: randomNonce(),
  };
  const message = canonicalJSON(body);
  const key = await crypto.subtle.importKey(
    'raw', new TextEncoder().encode(id.secret),
    { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'],
  );
  const sig = toHex(
    await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(message)),
  );
  const r = await fetch(`${API_BASE}/feedback`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      'x-signature': sig,
    },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    let detail = 'submit failed';
    try {
      detail = String((await r.json()).error ?? detail);
    } catch { /* keep default */ }
    throw new Error(detail);
  }
  return Number((await r.json() as { id: number }).id);
}
